<template>
  <div v-if="visible" class="image-viewer-overlay" @click="handleMaskClick">
    <div class="image-viewer-container" @click.stop>
      <!-- 顶部工具栏 -->
      <div class="image-viewer-toolbar">
        <div class="toolbar-left">
          <span class="image-title">{{ (currentImage && currentImage.title) || '图片预览' }}</span>
        </div>
        <div class="toolbar-right">
          <span class="image-counter">{{ currentIndex + 1 }} / {{ images.length }}</span>
          <button class="toolbar-btn" @click="$emit('close')">
            <i class="el-icon-close"></i>
          </button>
        </div>
      </div>

      <!-- 图片显示区域 -->
      <div class="image-display-area" ref="imageContainer">
        <!-- 左侧导航按钮 -->
        <button 
          v-if="images.length > 1"
          class="side-nav-btn left-nav-btn"
          :disabled="currentIndex === 0"
          @click="prevImage"
        >
          <i class="el-icon-arrow-left"></i>
        </button>

        <!-- 图片容器，支持左右切换效果 -->
        <div class="image-container" :style="containerStyles">
          <img 
            ref="imageElement"
            :src="currentImageUrl" 
            :alt="(currentImage && currentImage.title) || '图片'"
            :style="imageStyles"
            @load="handleImageLoad"
            @error="handleImageError"
            draggable="false"
            @mousedown="handleMouseDown"
          />
        </div>
        
        <!-- 右侧导航按钮 -->
        <button 
          v-if="images.length > 1"
          class="side-nav-btn right-nav-btn"
          :disabled="currentIndex === images.length - 1"
          @click="nextImage"
        >
          <i class="el-icon-arrow-right"></i>
        </button>
        
        <!-- 加载中 -->
        <div v-if="imageLoading" class="image-loading">
          <i class="el-icon-loading"></i>
          <span>加载中...</span>
        </div>
        
        <!-- 错误提示 -->
        <div v-if="imageError" class="image-error">
          <i class="el-icon-warning"></i>
          <span>图片加载失败</span>
        </div>
      </div>

      <!-- 底部控制栏 -->
      <div class="image-controls">
        <!-- 主要控制按钮 -->
        <div class="main-controls">
          <button class="control-btn" @click="zoomOut" :disabled="scale <= 0.1">
            <i class="el-icon-zoom-out"></i>
            <span>缩小</span>
          </button>
          
          <button class="control-btn" @click="zoomIn" :disabled="scale >= 5">
            <i class="el-icon-zoom-in"></i>
            <span>放大</span>
          </button>
          
          <button class="control-btn" @click="resetScale">
            <i class="el-icon-refresh"></i>
            <span>1:1</span>
          </button>
          
          <button class="control-btn" @click="rotateLeft">
            <i class="el-icon-refresh-left"></i>
            <span>逆时针</span>
          </button>
          
          <button class="control-btn" @click="rotateRight">
            <i class="el-icon-refresh-right"></i>
            <span>顺时针</span>
          </button>
          
          <button class="control-btn" @click="toggleFullscreen">
            <i :class="isFullscreen ? 'el-icon-copy-document' : 'el-icon-full-screen'"></i>
            <span>{{ isFullscreen ? '退出全屏' : '全屏' }}</span>
          </button>
          
          <!-- 抠图按钮 -->
          <button 
            class="control-btn matting-btn" 
            @click="performMatting"
            :disabled="mattingLoading"
          >
            <i :class="mattingLoading ? 'el-icon-loading' : 'el-icon-scissors'"></i>
            <span>{{ mattingLoading ? '抠图中...' : '抠图' }}</span>
          </button>
          
          <!-- 下载按钮 -->
          <button class="control-btn" @click="downloadImage">
            <i class="el-icon-download"></i>
            <span>{{ isAuthenticated ? '下载原图' : '登录后下载原图' }}</span>
          </button>
        </div>

        <!-- 缩放显示 -->
        <div class="zoom-info">
          {{ Math.round(scale * 100) }}%
        </div>
      </div>
    </div>

    <!-- 抠图结果预览对话框 -->
    <el-dialog
      title="抠图结果"
      :visible.sync="mattingResultVisible"
      width="80%"
      center
      :before-close="closeMattingResult"
    >
      <div class="matting-result-container">
        <div class="result-images">
          <div class="original-image">
            <h4>原图</h4>
            <img :src="currentImageUrl" alt="原图" />
          </div>
          <div class="matted-image">
            <h4>抠图结果</h4>
            <div class="matted-image-wrapper">
              <img 
                v-if="mattingResult" 
                :src="mattingResult.processedImageUrl" 
                alt="抠图结果"
                class="result-image"
              />
              <div v-else class="no-result">
                <i class="el-icon-picture"></i>
                <span>暂无结果</span>
              </div>
            </div>
          </div>
        </div>
        
        <div class="result-actions">
          <el-button @click="closeMattingResult">关闭</el-button>
          <el-button 
            type="primary" 
            @click="downloadMattingResult"
            :disabled="!mattingResult"
          >
            下载抠图结果
          </el-button>
          <el-button 
            type="success" 
            @click="saveMattingResult"
            :disabled="!mattingResult || savingResult"
            :loading="savingResult"
          >
            保存到相册
          </el-button>
        </div>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { apiClient } from '@/services/api';
import { isSignedCosUrl } from '@/utils/assetUrl';
import { mapState } from 'vuex';

export default {
  name: 'ImageViewer',
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    images: {
      type: Array,
      default: () => []
    },
    initialIndex: {
      type: Number,
      default: 0
    }
  },
  data() {
    return {
      currentIndex: 0,
      scale: 0.4, // 设置初始缩放为0.4，适中的初始显示
      rotation: 0,
      translateX: 0,
      translateY: 0,
      isFullscreen: false,
      imageLoading: false,
      imageError: false,
      isDragging: false,
      lastMouseX: 0,
      lastMouseY: 0,
      mattingLoading: false,
      mattingResult: null,
      mattingResultVisible: false,
      savingResult: false,
      imageContainerRef: null // 保存 imageContainer 引用以便清理
    };
  },
  computed: {
    ...mapState(['isAuthenticated']),
    currentImage() {
      return this.images[this.currentIndex] || null;
    },
    currentImageUrl() {
      if (!this.currentImage) return '';
      if (typeof this.currentImage === 'string') return this.currentImage;
      return this.currentImage.viewerUrl ||
        this.currentImage.optimizedUrl ||
        this.currentImage.displayUrl ||
        this.currentImage.bestUrl ||
        this.currentImage.thumbnailUrl ||
        this.currentImage.originalUrl ||
        this.currentImage.url ||
        '';
    },
    currentOriginalDownloadUrl() {
      if (!this.currentImage) return '';
      if (typeof this.currentImage === 'string') return this.currentImage;
      return this.currentImage.originalDownloadUrl ||
        this.currentImage.originalUrl ||
        this.currentImage.url ||
        this.currentImage.largeUrl ||
        this.currentImage.bestUrl ||
        this.currentImage.viewerUrl ||
        '';
    },
    imageStyles() {
      return {
        transform: `scale(${this.scale}) rotate(${this.rotation}deg) translate(${this.translateX}px, ${this.translateY}px)`,
        transformOrigin: 'center center',
        // 移除transition，避免缩放翻页效果
        transition: this.isDragging ? 'none' : 'none',
        cursor: this.isDragging ? 'grabbing' : 'grab',
        maxWidth: 'none',
        maxHeight: 'none'
      };
    },
    containerStyles() {
      return {
        transition: 'opacity 0.2s ease-in-out',
        opacity: this.imageLoading ? 0.7 : 1
      };
    }
  },
  watch: {
    visible(newVal) {
      if (newVal) {
        this.currentIndex = this.initialIndex;
        // 移除resetTransform()，保持我们设置的初始缩放值
        // this.resetTransform();
        document.addEventListener('keydown', this.handleKeyDown);
        // 注意：wheel事件监听器应该添加到图片容器上，而不是整个document
        // 这样可以避免阻止页面的正常滚动
        this.$nextTick(() => {
          const imageContainer = this.$refs.imageContainer;
          if (imageContainer) {
            // 保存引用以便后续清理
            this.imageContainerRef = imageContainer;
            imageContainer.addEventListener('wheel', this.handleWheel, { passive: false });
            console.log('✅ ImageViewer: 已添加 wheel 监听器到 imageContainer');
          }
        });
      } else {
        document.removeEventListener('keydown', this.handleKeyDown);
        // 移除图片容器上的wheel监听器 - 使用保存的引用
        if (this.imageContainerRef) {
          this.imageContainerRef.removeEventListener('wheel', this.handleWheel);
          console.log('✅ ImageViewer: 已移除 wheel 监听器从 imageContainer');
          this.imageContainerRef = null;
        }
        // 额外的安全措施：也尝试从 $refs 移除
        const imageContainer = this.$refs.imageContainer;
        if (imageContainer) {
          imageContainer.removeEventListener('wheel', this.handleWheel);
        }
        this.exitFullscreen();
      }
    },
    currentIndex() {
      // 移除resetTransform()，避免缩放翻页效果
      // this.resetTransform();
      this.imageLoading = true;
      this.imageError = false;
    }
  },
  methods: {
    // 图片加载处理
    handleImageLoad() {
      // 添加短暂延迟，让切换效果更流畅
      setTimeout(() => {
        this.imageLoading = false;
        this.imageError = false;
      }, 100);
    },
    
    handleImageError() {
      this.imageLoading = false;
      this.imageError = true;
    },
    
    // 自动适应图片大小
    autoFitImage() {
      this.$nextTick(() => {
        const img = this.$refs.imageElement;
        const container = this.$refs.imageContainer;
        
        if (!img || !container) return;
        
        // 如果缩放值已经是自定义的（小于1），则不自动适应
        if (this.scale < 1) {
          return;
        }
        
        const containerWidth = container.clientWidth;
        const containerHeight = container.clientHeight;
        const imageWidth = img.naturalWidth;
        const imageHeight = img.naturalHeight;
        
        // 计算适合的缩放比例
        const scaleX = (containerWidth * 0.9) / imageWidth;
        const scaleY = (containerHeight * 0.9) / imageHeight;
        
        this.scale = Math.min(scaleX, scaleY, 1); // 最大不超过原始大小
      });
    },
    
    // 导航控制
    prevImage() {
      if (this.currentIndex > 0) {
        this.imageLoading = true;
        this.currentIndex--;
      }
    },
    
    nextImage() {
      if (this.currentIndex < this.images.length - 1) {
        this.imageLoading = true;
        this.currentIndex++;
      }
    },
    
    // 缩放控制
    zoomIn() {
      this.scale = Math.min(this.scale * 1.2, 5);
    },
    
    zoomOut() {
      this.scale = Math.max(this.scale / 1.2, 0.1);
    },
    
    resetScale() {
      this.scale = 1;
      this.translateX = 0;
      this.translateY = 0;
    },
    
    // 旋转控制
    rotateLeft() {
      this.rotation -= 90;
    },
    
    rotateRight() {
      this.rotation += 90;
    },
    
    // 重置变换
    resetTransform() {
      this.scale = 1;
      this.rotation = 0;
      this.translateX = 0;
      this.translateY = 0;
    },
    
    // 全屏控制
    toggleFullscreen() {
      if (!this.isFullscreen) {
        this.enterFullscreen();
      } else {
        this.exitFullscreen();
      }
    },

    getFullscreenElement() {
      return document.fullscreenElement ||
        document.webkitFullscreenElement ||
        document.mozFullScreenElement ||
        document.msFullscreenElement ||
        null;
    },

    canControlFullscreen() {
      const isVisible = typeof document.visibilityState !== 'string' || document.visibilityState === 'visible';
      const hasFocus = typeof document.hasFocus !== 'function' || document.hasFocus();
      return isVisible && hasFocus;
    },

    async enterFullscreen() {
      const element = this.$el;
      if (!element || !this.canControlFullscreen()) {
        this.isFullscreen = !!this.getFullscreenElement();
        return;
      }

      try {
        if (element.requestFullscreen) {
          await element.requestFullscreen();
        } else if (element.webkitRequestFullscreen) {
          element.webkitRequestFullscreen();
        } else if (element.mozRequestFullScreen) {
          element.mozRequestFullScreen();
        } else if (element.msRequestFullscreen) {
          element.msRequestFullscreen();
        }
      } catch (error) {
        console.warn('进入全屏失败:', error);
      } finally {
        this.isFullscreen = !!this.getFullscreenElement();
      }
    },

    async exitFullscreen() {
      const fullscreenElement = this.getFullscreenElement();
      if (!fullscreenElement) {
        this.isFullscreen = false;
        return;
      }

      if (!this.canControlFullscreen()) {
        this.isFullscreen = false;
        return;
      }

      try {
        if (document.exitFullscreen) {
          await document.exitFullscreen();
        } else if (document.webkitExitFullscreen) {
          document.webkitExitFullscreen();
        } else if (document.mozCancelFullScreen) {
          document.mozCancelFullScreen();
        } else if (document.msExitFullscreen) {
          document.msExitFullscreen();
        }
      } catch (error) {
        console.warn('退出全屏失败:', error);
      } finally {
        this.isFullscreen = !!this.getFullscreenElement();
      }
    },
    
    // 鼠标拖拽
    handleMouseDown(event) {
      if (this.scale <= 1) return;
      
      this.isDragging = true;
      this.lastMouseX = event.clientX;
      this.lastMouseY = event.clientY;
      
      document.addEventListener('mousemove', this.handleMouseMove);
      document.addEventListener('mouseup', this.handleMouseUp);
      
      event.preventDefault();
    },
    
    handleMouseMove(event) {
      if (!this.isDragging) return;
      
      const deltaX = event.clientX - this.lastMouseX;
      const deltaY = event.clientY - this.lastMouseY;
      
      this.translateX += deltaX / this.scale;
      this.translateY += deltaY / this.scale;
      
      this.lastMouseX = event.clientX;
      this.lastMouseY = event.clientY;
    },
    
    handleMouseUp() {
      this.isDragging = false;
      document.removeEventListener('mousemove', this.handleMouseMove);
      document.removeEventListener('mouseup', this.handleMouseUp);
    },
    
    // 键盘事件
    handleKeyDown(event) {
      switch (event.key) {
        case 'Escape':
          this.$emit('close');
          break;
        case 'ArrowLeft':
          this.prevImage();
          break;
        case 'ArrowRight':
          this.nextImage();
          break;
        case '+':
        case '=':
          this.zoomIn();
          break;
        case '-':
          this.zoomOut();
          break;
        case '0':
          this.resetScale();
          break;
        case 'f':
        case 'F':
          this.toggleFullscreen();
          break;
      }
    },
    
    // 鼠标滚轮缩放
    handleWheel(event) {
      event.preventDefault();
      
      const delta = event.deltaY;
      if (delta > 0) {
        this.zoomOut();
      } else {
        this.zoomIn();
      }
    },
    
    // 遮罩点击
    handleMaskClick() {
      this.$emit('close');
    },
    
    // 下载图片
    async downloadImage() {
      try {
        if (!this.isAuthenticated) {
          this.$message.warning('请先登录后再下载原图');
          return;
        }

        const imageUrl = this.currentOriginalDownloadUrl;
        if (!imageUrl) {
          this.$message.error('原图地址不存在');
          return;
        }
        const filename = (this.currentImage && this.currentImage.filename) || `image_${Date.now()}.jpg`;
        
        // 使用直接链接下载，避免CORS问题
        const link = document.createElement('a');
        link.href = imageUrl;
        link.download = filename;
        link.target = '_blank';
        
        // 签名 URL 不能追加额外参数，否则会直接失效
        if (isSignedCosUrl(imageUrl)) {
          // 保持原始链接
        } else if (imageUrl.includes('?')) {
          link.href += `&_t=${Date.now()}`;
        } else {
          link.href += `?_t=${Date.now()}`;
        }
        
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        
        this.$message.success('图片下载成功');
      } catch (error) {
        console.error('下载图片失败:', error);
        this.$message.error('图片下载失败');
      }
    },
    
    // 抠图功能
    async performMatting() {
      if (!this.currentImage) {
        this.$message.error('没有选中的图片');
        return;
      }
      
      this.mattingLoading = true;
      
      try {
        const response = await apiClient.post('/image/remove-background', {
          imageUrl: this.currentImageUrl,
          imageId: this.currentImage.id
        });
        
        if (response.status === 'success') {
          this.mattingResult = response.data;
          this.mattingResultVisible = true;
          this.$message.success('抠图成功！');
        } else {
          throw new Error(response.message || '抠图失败');
        }
      } catch (error) {
        console.error('抠图失败:', error);
        this.$message.error((error.response && error.response.data && error.response.data.message) || '抠图失败，请稍后重试');
      } finally {
        this.mattingLoading = false;
      }
    },
    
    // 关闭抠图结果对话框
    closeMattingResult() {
      this.mattingResultVisible = false;
      this.mattingResult = null;
    },
    
    // 下载抠图结果
    async downloadMattingResult() {
      if (!this.mattingResult) return;
      
      try {
        const processedImageUrl = this.mattingResult.processedImageUrl;
        const filename = `matted_${(this.currentImage && this.currentImage.filename) || Date.now()}.png`;
        
        // 使用直接链接下载，避免CORS问题
        const link = document.createElement('a');
        link.href = processedImageUrl;
        link.download = filename;
        link.target = '_blank';
        
        // 签名 URL 不能追加额外参数，否则会直接失效
        if (isSignedCosUrl(processedImageUrl)) {
          // 保持原始链接
        } else if (processedImageUrl.includes('?')) {
          link.href += `&_t=${Date.now()}`;
        } else {
          link.href += `?_t=${Date.now()}`;
        }
        
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        
        this.$message.success('抠图结果下载成功');
      } catch (error) {
        console.error('下载抠图结果失败:', error);
        this.$message.error('下载失败');
      }
    },
    
    // 保存抠图结果到相册
    async saveMattingResult() {
      if (!this.mattingResult) return;
      
      this.savingResult = true;
      
      try {
        const response = await apiClient.post('/image/save-processed', {
          originalImageId: this.currentImage && this.currentImage.id,
          processedImageUrl: this.mattingResult.processedImageUrl,
          processType: 'remove_background',
          title: `${(this.currentImage && this.currentImage.title) || '抠图'} - 背景移除`
        });
        
        if (response.status === 'success') {
          this.$message.success('抠图结果已保存到相册');
          this.closeMattingResult();
        } else {
          throw new Error(response.message || '保存失败');
        }
      } catch (error) {
        console.error('保存抠图结果失败:', error);
        this.$message.error((error.response && error.response.data && error.response.data.message) || '保存失败，请稍后重试');
      } finally {
        this.savingResult = false;
      }
    }
  },
  
  beforeDestroy() {
    // 清理事件监听器
    document.removeEventListener('keydown', this.handleKeyDown);
    document.removeEventListener('mousemove', this.handleMouseMove);
    document.removeEventListener('mouseup', this.handleMouseUp);
    
    // 清理 wheel 监听器 - 从正确的元素移除
    if (this.imageContainerRef) {
      this.imageContainerRef.removeEventListener('wheel', this.handleWheel);
      console.log('✅ ImageViewer: beforeDestroy 清理 wheel 监听器');
      this.imageContainerRef = null;
    }
    
    // 额外的安全措施：也尝试从 $refs 移除
    const imageContainer = this.$refs.imageContainer;
    if (imageContainer) {
      imageContainer.removeEventListener('wheel', this.handleWheel);
    }
    
    // 确保恢复页面滚动
    if (document.body.style.overflow === 'hidden') {
      document.body.style.overflow = '';
      console.log('✅ ImageViewer: 恢复页面滚动');
    }
  }
};
</script>

<style scoped>
.image-viewer-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.9);
  z-index: 2000;
  display: flex;
  flex-direction: column;
  user-select: none;
}

.image-viewer-container {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
}

/* 顶部工具栏 */
.image-viewer-toolbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 20px;
  background-color: rgba(0, 0, 0, 0.7);
  backdrop-filter: blur(10px);
  color: white;
}

.toolbar-left .image-title {
  font-size: 16px;
  font-weight: 500;
}

.toolbar-right {
  display: flex;
  align-items: center;
  gap: 16px;
}

.image-counter {
  font-size: 14px;
  color: #ccc;
}

.toolbar-btn {
  background: none;
  border: none;
  color: white;
  font-size: 18px;
  cursor: pointer;
  padding: 4px;
  border-radius: 4px;
  transition: background-color 0.3s;
}

.toolbar-btn:hover {
  background-color: rgba(255, 255, 255, 0.1);
}

/* 图片显示区域 */
.image-display-area {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  overflow: hidden;
}

.image-container {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  position: relative;
}

.image-container img {
  max-width: 90%;
  max-height: 90%;
  object-fit: contain;
}

.side-nav-btn {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  background-color: rgba(0, 0, 0, 0.6);
  border: 2px solid rgba(255, 255, 255, 0.3);
  color: white;
  font-size: 28px;
  cursor: pointer;
  padding: 16px;
  border-radius: 50%;
  z-index: 10;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  backdrop-filter: blur(10px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
  width: 60px;
  height: 60px;
}

.side-nav-btn:hover:not(:disabled) {
  background-color: rgba(255, 255, 255, 0.15);
  border-color: rgba(255, 255, 255, 0.5);
  transform: translateY(-50%) scale(1.1);
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.4);
}

.side-nav-btn:active:not(:disabled) {
  transform: translateY(-50%) scale(0.95);
}

.side-nav-btn:disabled {
  opacity: 0.3;
  cursor: not-allowed;
  transform: translateY(-50%) scale(0.9);
}

.left-nav-btn {
  left: 30px;
}

.right-nav-btn {
  right: 30px;
}

.image-loading,
.image-error {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  display: flex;
  flex-direction: column;
  align-items: center;
  color: white;
  font-size: 16px;
  z-index: 10;
}

.image-loading i,
.image-error i {
  font-size: 48px;
  margin-bottom: 12px;
}

.image-loading i {
  animation: rotating 2s linear infinite;
}

@keyframes rotating {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

/* 底部控制栏 */
.image-controls {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 16px 20px;
  background-color: rgba(0, 0, 0, 0.7);
  backdrop-filter: blur(10px);
  position: relative;
}

.navigation-controls,
.main-controls {
  display: flex;
  gap: 8px;
}

.main-controls {
  justify-content: center;
  flex-wrap: wrap;
}

.control-btn {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 8px 12px;
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  color: white;
  border-radius: 6px;
  cursor: pointer;
  font-size: 12px;
  transition: all 0.3s;
  min-width: 60px;
}

.control-btn:hover:not(:disabled) {
  background: rgba(255, 255, 255, 0.2);
  border-color: rgba(255, 255, 255, 0.3);
}

.control-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.control-btn i {
  font-size: 18px;
  margin-bottom: 4px;
}

.control-btn span {
  font-size: 11px;
  line-height: 1;
}

.matting-btn {
  background: linear-gradient(135deg, #ff6b6b, #ee5a24);
  border-color: #ff6b6b;
}

.matting-btn:hover:not(:disabled) {
  background: linear-gradient(135deg, #ff5252, #e55100);
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(255, 107, 107, 0.3);
}

.zoom-info {
  color: #ccc;
  font-size: 14px;
  min-width: 50px;
  text-align: center;
  position: absolute;
  right: 20px;
  top: 50%;
  transform: translateY(-50%);
}

/* 抠图结果对话框 */
.matting-result-container {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.result-images {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
}

.original-image,
.matted-image {
  text-align: center;
}

.original-image h4,
.matted-image h4 {
  margin: 0 0 12px 0;
  color: #333;
  font-size: 16px;
  font-weight: 600;
}

.original-image img,
.matted-image img {
  max-width: 100%;
  max-height: 300px;
  object-fit: contain;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.matted-image-wrapper {
  background: linear-gradient(45deg, #f0f0f0 25%, transparent 25%), 
              linear-gradient(-45deg, #f0f0f0 25%, transparent 25%), 
              linear-gradient(45deg, transparent 75%, #f0f0f0 75%), 
              linear-gradient(-45deg, transparent 75%, #f0f0f0 75%);
  background-size: 20px 20px;
  background-position: 0 0, 0 10px, 10px -10px, -10px 0px;
  background-color: white;
  padding: 20px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 200px;
}

.result-image {
  border-radius: 8px;
}

.no-result {
  display: flex;
  flex-direction: column;
  align-items: center;
  color: #999;
}

.no-result i {
  font-size: 48px;
  margin-bottom: 8px;
}

.result-actions {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding-top: 16px;
  border-top: 1px solid #eee;
}

/* 响应式设计 */
@media (max-width: 768px) {
  .image-viewer-toolbar {
    padding: 8px 12px;
  }
  
  .toolbar-left .image-title {
    font-size: 14px;
  }
  
  .image-controls {
    padding: 12px;
    flex-wrap: wrap;
    gap: 12px;
  }
  
  .zoom-info {
    right: 12px;
  }
  
  .navigation-controls,
  .main-controls {
    gap: 6px;
  }
  
  .control-btn {
    padding: 6px 8px;
    min-width: 50px;
  }
  
  .control-btn i {
    font-size: 16px;
  }
  
  .control-btn span {
    font-size: 10px;
  }
  
  /* 侧边导航按钮在平板上的样式 */
  .side-nav-btn {
    width: 50px;
    height: 50px;
    font-size: 24px;
    padding: 12px;
  }
  
  .left-nav-btn {
    left: 20px;
  }
  
  .right-nav-btn {
    right: 20px;
  }
  
  .result-images {
    grid-template-columns: 1fr;
    gap: 16px;
  }
  
  .result-actions {
    flex-direction: column;
  }
}

@media (max-width: 480px) {
  .main-controls {
    flex-wrap: wrap;
    justify-content: center;
  }
  
  .image-controls {
    flex-direction: column;
    gap: 8px;
  }
  
  .zoom-info {
    position: static;
    transform: none;
    margin-top: 8px;
  }
  
  /* 侧边导航按钮在手机上的样式 */
  .side-nav-btn {
    width: 40px;
    height: 40px;
    font-size: 20px;
    padding: 8px;
  }
  
  .left-nav-btn {
    left: 10px;
  }
  
  .right-nav-btn {
    right: 10px;
  }
}
</style> 